import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { LabelText } from "./LabelDecoration";

interface RichLabelProps {
    children: string | null | undefined;
    className?: string;
}

/**
 * RichLabel renders text with markdown and HTML formatting support.
 *
 * Supported formats:
 * - **bold** or <strong>bold</strong>
 * - *italic* or _italic_ or <em>italic</em>
 * - <u>underline</u>
 * - ++underline++ (custom syntax, converted to <u> tags)
 *
 * Used for input_label fields in questionnaires where certain words need emphasis
 * per ePRO migration guidelines (criterion 1.4: formatting preserved).
 *
 * @example
 * // In SQL seed file:
 * 'How would you describe the **pain** you _usually_ have?'
 * 'Have you been ++limping++ when walking?'
 */
export function RichLabel({ children, className }: RichLabelProps) {
    if (!children) {
        return null;
    }
    // Review screens can mark this text (e.g. with a suggested change); otherwise it is the markdown below.
    return <LabelText fallback={<MarkdownLabel className={className}>{children}</MarkdownLabel>}>{children}</LabelText>;
}

function MarkdownLabel({ children, className }: { children: string; className?: string }) {
    // "5. Squatting" is a question number, not a markdown list. The list marker would be
    // dropped because lists are not allowed in these labels.
    const processedText = children.replace(/^(\d+)\.(?=\s)/gm, "$1\\.").replace(/\+\+([^+]+)\+\+/g, "<u>$1</u>");

    return (
        <Markdown
            className={className}
            rehypePlugins={[rehypeRaw]}
            components={{
                // Override paragraph to render inline (no wrapping <p> tags)
                p: ({ children }) => <>{children}</>,
                strong: ({ children }) => <strong className="font-bold text-slate-950">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                u: ({ children }) => <u className="underline">{children}</u>,
            }}
            allowedElements={["p", "strong", "em", "u"]}
            unwrapDisallowed
        >
            {processedText}
        </Markdown>
    );
}

/** Shown under edit fields so asterisks look like typing syntax, not a broken label. */
export function MarkdownFormatHint({ extra }: { extra?: string }) {
    return (
        <p className="text-2xs text-muted-foreground">
            Wrap a word in two asterisks for <strong>bold</strong>, or one asterisk for <em>italics</em>.
            {extra ? ` ${extra}` : ""}
        </p>
    );
}

export default RichLabel;
